import React, { useEffect, useMemo, useState } from 'react';
import { CustomCheckBox, Input } from 'components/input';
import CheckboxBeforeLabel from 'components/searchAndFilter/components/CheckboxBeforeLabel';
import { useRMDispatch } from 'redux/hooks';
import {
  resetFilterSelectedValues,
  resetSearchFilterValues,
} from 'redux/reducers/searchnFilterReducer';
import { sortArray } from 'utils/Global';
import {
  formatDateDisplay,
  getFilterValueDTO,
  getFilteredFiltersByValuePredicate,
} from '../utils';
import { ValueType } from '../constants';
import { useSelectedSearchFilter } from 'redux/hooks/searchnFilter';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import PanelFooter from 'components/searchAndFilter/components/panelFooter';
import { IStyle } from 'Styles';

interface Props {
  title: string;
  filterId: number;
  valueType?: string;
  modalFooter?: React.ReactNode;
  hideModal?: () => void;
}
const ValuesSelectionModalBody = (props: Props) => {
  const { title, filterId, valueType, hideModal } = props;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const filter = useSelectedSearchFilter(filterId);
  const dispatch = useRMDispatch();

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [unselectedValues, setUnselectedValues] = useState<string[]>([]);
  const [originalSelectedValues, setOriginalSelectedValues] = useState<string[]>([]);
  const [originalUnselectedValues, setOriginalUnselectedValues] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>();
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const searchChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleToggleSelectAll = () => {
    if (!selectAll) {
      setSelectedValues([...originalSelectedValues, ...originalUnselectedValues]);
      setUnselectedValues([]);
    } else {
      setSelectedValues([]);
      setUnselectedValues([...originalSelectedValues, ...originalUnselectedValues]);
    }
    setSelectAll(!selectAll);
    setIsDirty(true);
  };

  const toggleValueSelection = (_text: string) => {
    const text = _text === 'None' ? null : _text;
    if (selectedValues.includes(text)) {
      setSelectedValues(selectedValues.filter((value) => value !== text));
      setUnselectedValues([...unselectedValues, text]);
    } else {
      setUnselectedValues(unselectedValues.filter((value) => value !== text));
      setSelectedValues([...selectedValues, text]);
    }
    setIsDirty(true);
  };

  const handleApplyClick = () => {
    dispatch(
      resetSearchFilterValues({
        filterId: filter.id,
        values: selectedValues?.map((v) => getFilterValueDTO(filter.id, v)),
      }),
    );
    dispatch(
      resetFilterSelectedValues({
        filterId: filter.id,
        values: sortArray(selectedValues),
      }),
    );
    hideModal();
  };

  const handleResetClick = () => {
    setSelectedValues(originalSelectedValues);
    setUnselectedValues(originalUnselectedValues);
    setIsDirty(false);
  };

  const modalFooterButtons: RMCustomButtonProps[] = [
    {
      title: 'RESET',
      onClick: handleResetClick,
      isDisabled: !isDirty,
      contentContainerStyle: styles.reset,
    },
    {
      title: 'CANCEL',
      onClick: hideModal,
      isDisabled: false,
      contentContainerStyle: styles.cancel,
    },
    {
      title: 'APPLY',
      onClick: handleApplyClick,
      isDisabled: !isDirty,
      contentContainerStyle: isDirty ? styles.applyEnabled : styles.applyDisabled,
    },
  ];

  const filteredSelectedValues = useMemo(() => {
    return selectedValues.filter((value) =>
      getFilteredFiltersByValuePredicate(value, searchTerm),
    );
  }, [selectedValues, searchTerm]);

  const filteredUnselectedValues = useMemo(() => {
    return unselectedValues.filter((value) =>
      getFilteredFiltersByValuePredicate(value, searchTerm),
    );
  }, [unselectedValues, searchTerm]);

  useEffect(() => {
    if (!filter?.allValues) {
      return;
    }

    const selected = [];
    const unselected = [];

    filter.allValues.forEach((value) => {
      const val = value
        ? valueType === ValueType.DATE || valueType === ValueType.DATE_IMAGED
          ? formatDateDisplay(value)
          : value.toString()
        : null;

      if (filter.selectedValues?.includes(val)) {
        selected.push(val);
      } else if (!filter.selectedValues?.includes(val)) {
        unselected.push(val);
      }
    });

    setSelectedValues(selected);
    setUnselectedValues(unselected);
    setOriginalSelectedValues(selected);
    setOriginalUnselectedValues(unselected);
  }, [filter.allValues, filter.selectedValues, valueType]);

  useEffect(() => {
    setSelectAll(selectedValues.length === filter?.allValues?.length);
  }, [selectedValues, filter?.allValues]);

  return (
    <>
      <div
        id="valuesselectionmodalbody-wrapper"
        className="h-[58vh] w-full border flex flex-col pr-[8px] pl-[8px] pt-[4px] pb-[4px]"
      >
        <div
          id="valuesselectionmodalbody-searchbox-wrapper"
          className="flex flex-row justify-between gap-[8px] items-center"
        >
          <div
            id="valuesselectionmodalbody-searhbox-title"
            className="w-7/10 text-[14px] leading-[19px] font-semibold"
          >
            {title}
          </div>
          <div id="valuesselectionmodalbody-searchbox-filter" className="w-3/10">
            <Input
              id="valuesselectionmodalbody-searchbox-filter-input"
              inputType="text"
              name={'searchBox'}
              placeHolder={'Filter by text...'}
              onChange={searchChangeHandler}
              value={searchTerm}
            />
          </div>
        </div>
        <CustomCheckBox
          id="valuesselectionmodalbody-select-all-checkbox"
          type="checkbox"
          checked={selectAll}
          onChange={handleToggleSelectAll}
          label="Select all"
        />
        <div
          id="valuesselectionmodalbody-checkboxes-wrapper"
          className="flex flex-col max-h-[50vh]"
        >
          <div
            id="valuesselectionmodalbody-checkboxes-container"
            className="overflow-y-scroll w-full max-h-[53vh]"
          >
            <div
              id="valuesselectionmodalbody-checkboxes-selected-values"
              className="grid grid-cols-2 items-center"
            >
              {sortArray(filteredSelectedValues ?? selectedValues)?.map((sf, index) => (
                <CheckboxBeforeLabel
                  key={index}
                  containerClass="h-[40px] gap-[10px] w-[100%] p-[4px]"
                  labelClass="mb-0"
                  isChecked={true}
                  isVisible={true}
                  columnId={sf ?? 'None'}
                  columnTitle={sf ?? 'None'}
                  onCheckboxChange={toggleValueSelection}
                />
              ))}
            </div>
            <hr
              id="valuesselectionmodalbody-checkboxes-hr-line"
              className="w-full bg-gray-500 block"
            />
            <div
              id="valuesselectionmodalbody-checkboxed-unselected-values"
              className="grid grid-cols-2 items-center"
            >
              {sortArray(filteredUnselectedValues ?? unselectedValues)?.map(
                (sf, index) => (
                  <CheckboxBeforeLabel
                    key={index}
                    containerClass="h-[40px] gap-[10px] w-[100%] p-[4px]"
                    labelClass="mb-0"
                    isChecked={false}
                    isVisible={true}
                    columnId={sf ? sf.toString() : 'None'}
                    columnTitle={sf ? sf.toString() : 'None'}
                    onCheckboxChange={toggleValueSelection}
                  />
                ),
              )}
            </div>
          </div>
        </div>
      </div>
      <PanelFooter buttons={modalFooterButtons} containerClass="my-2" />
    </>
  );
};

export default ValuesSelectionModalBody;

const styles: IStyle = {
  reset: {
    background: '#313131',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
    color: 'white',
    fontSize: '15px',
  },
  cancel: {
    color: '#4caf50 ',
    background: '#ffffff',
    borderStyle: 'solid',
    borderColor: '#4caf50',
    borderWidth: '1px',
    borderRadius: '4px',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    fontSize: '15px',
  },
  applyDisabled: {
    background: '#CACACA',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
    fontSize: '15px',
  },
  applyEnabled: {
    background: '#4CAF50',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
    color: 'white',
    fontSize: '15px',
  },
};
